import { combineReducers, createSlice, PayloadAction } from '@reduxjs/toolkit'

import { makeFetchSlice, fetchEdgeDataThunk } from 'app/redux-fetch'
import { AppDispatch } from 'app/store'

import {
  fetchDomainThunk,
  fetchDomainSubDomainsThunk,
} from 'components/DomainBreadcrumbs/slice'

import {
  fetchDomain,
  FetchDomainParams,
  FetchDomainJobsParams,
  fetchDomainJobsWithHome,
  fetchDomainJobsWithHomeCount,
  fetchDomainPendingJobs,
  fetchRenameDomain,
  fetchDeleteDomain,
  fetchDomainDealers,
  fetchDomainExport,
} from './api'

import {
  DOMAIN_TO_SUB_DOMAINS_CACHE_REDUCER_NAME,
  DOMAIN_TO_DOMAIN_DATA_CACHE_REDUCER_NAME,
  SELECTED_NAV_DOMAIN_REDUCER_NAME,
  DOMAIN_JOBS_WITH_HOME_REDUCER_NAME,
  DOMAIN_JOBS_WITH_HOME_COUNT_REDUCER_NAME,
  SELECTED_NAV_TOP_DOMAIN_REDUCER_NAME,
  DOMAIN_PENDING_JOBS_REDUCER_NAME,
  DOMAIN_DEALERS_REDUCER_NAME,
  DOMAIN_EXPORT_REDUCER_NAME
} from './consts'

import { DomainType, RenameDomainParams } from './types'

const selectedNavTopDomainInitialState = {} as DomainType

export const selectedNavTopDomainSlice = createSlice({
  name: SELECTED_NAV_TOP_DOMAIN_REDUCER_NAME,
  initialState: selectedNavTopDomainInitialState,
  reducers: {
    setNavTopDomain: (_, action: PayloadAction<DomainType>) => action.payload,
    clear: () => selectedNavTopDomainInitialState,
  },
})
export const selectedNavTopDomainActions = selectedNavTopDomainSlice.actions
export const selectedNavTopDomainReducer = selectedNavTopDomainSlice.reducer

const selectedNavDomainSlice = makeFetchSlice(SELECTED_NAV_DOMAIN_REDUCER_NAME)
export const selectedNavDomainReducer = selectedNavDomainSlice.reducer
export const selectedNavDomainActions = selectedNavDomainSlice.actions

export const fetchSelectedNavDomainThunk = (
  domainId: string,
  params?: FetchDomainParams
) => {
  return async (dispatch: AppDispatch) => {
    try {
      const resp = await fetchEdgeDataThunk({
        dispatch,
        actions: selectedNavDomainActions,
        asyncFunc: () => fetchDomain(domainId, params),
      })
      return resp
    } catch (error) {
      console.log('Error fetching selected domain', error)
    }
  }
}

const domainJobsWithHomeSlice = makeFetchSlice(
  DOMAIN_JOBS_WITH_HOME_REDUCER_NAME
)
export const domainJobsWithHomeReducer = domainJobsWithHomeSlice.reducer
export const domainJobsWithHomesActions = domainJobsWithHomeSlice.actions

export const fetchDomainJobsWithHomeThunk = (
  domainId: string,
  params?: FetchDomainJobsParams
) => {
  return async (dispatch: AppDispatch) => {
    try {
      await fetchEdgeDataThunk({
        dispatch,
        actions: domainJobsWithHomesActions,
        asyncFunc: () => fetchDomainJobsWithHome(domainId, params),
      })
    } catch (error) {
      console.log('Error fetching domain jobs with homes', error)
    }
  }
}

const domainJobsWithHomeCountSlice = makeFetchSlice(
  DOMAIN_JOBS_WITH_HOME_COUNT_REDUCER_NAME
)
export const domainJobsWithHomeCountReducer =
  domainJobsWithHomeCountSlice.reducer
export const domainJobsWithHomesCountActions =
  domainJobsWithHomeCountSlice.actions

export const fetchDomainJobsWithHomeCountThunk = (
  domainId: string,
  params?: FetchDomainJobsParams
) => {
  return async (dispatch: AppDispatch) => {
    try {
      await fetchEdgeDataThunk({
        dispatch,
        actions: domainJobsWithHomesCountActions,
        asyncFunc: () => fetchDomainJobsWithHomeCount(domainId, params),
      })
    } catch (error) {
      console.log('Error fetching domain jobs with homes', error)
    }
  }
}

export const domainPendingJobsSlice = makeFetchSlice(
  DOMAIN_PENDING_JOBS_REDUCER_NAME
)
export const domainPendingJobsReducer = domainPendingJobsSlice.reducer
export const domainPendingJobsActions = domainPendingJobsSlice.actions

export const fetchDomainPendingJobsThunk = (domainId: string) => {
  return async (dispatch: AppDispatch) => {
    try {
      await fetchEdgeDataThunk({
        dispatch,
        actions: domainPendingJobsActions,
        asyncFunc: () => fetchDomainPendingJobs(domainId),
      })
    } catch (error) {
      console.log("Error fetching domain's pending jobs", error)
    }
  }
}

const domainToSubDomainsCacheSlice = createSlice({
  initialState: {} as Record<string, DomainType[]>,
  name: DOMAIN_TO_SUB_DOMAINS_CACHE_REDUCER_NAME,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchDomainSubDomainsThunk.fulfilled, (state, action) => {
      const {
        meta: { arg: domainId },
        payload: subDomains,
      } = action
      //console.log('domainToSubDomainsCacheSlice')
      state[domainId] = subDomains
    })
  },
})

const domainToSubDomainsCacheReducer = domainToSubDomainsCacheSlice.reducer

const domainToDomainDataCacheSlice = createSlice({
  initialState: {} as Record<string, DomainType | undefined>,
  name: DOMAIN_TO_DOMAIN_DATA_CACHE_REDUCER_NAME,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchDomainThunk.fulfilled, (state, action) => {
      const {
        meta: { arg: domainId },
        payload: domain,
      } = action

      state[domainId] = domain
    })
  },
})

const domainToDomainDataCacheReducer = domainToDomainDataCacheSlice.reducer

export const domainsCacheReducer = combineReducers({
  [DOMAIN_TO_SUB_DOMAINS_CACHE_REDUCER_NAME]: domainToSubDomainsCacheReducer,
  [DOMAIN_TO_DOMAIN_DATA_CACHE_REDUCER_NAME]: domainToDomainDataCacheReducer,
})

export const renameDomainThunk = (params: RenameDomainParams) => {
  return async (dispatch: AppDispatch) => {
    return await fetchEdgeDataThunk({
      dispatch,
      asyncFunc: () => fetchRenameDomain(params),
    })
  }
}

export const deleteDomainThunk = (domainId: string) => {
  return async (dispatch: AppDispatch) => {
    return await fetchEdgeDataThunk({
      dispatch,
      asyncFunc: () => fetchDeleteDomain(domainId),
    })
  }
}

const domainDealersSlice = makeFetchSlice(DOMAIN_DEALERS_REDUCER_NAME)
export const domainDealersReducer = domainDealersSlice.reducer
export const domainDealersActions = domainDealersSlice.actions

export const fetchDomainDealersThunk = (domainId: string) => {
  return async (dispatch: AppDispatch) =>
    await fetchEdgeDataThunk({
      dispatch,
      actions: domainDealersActions,
      asyncFunc: () => fetchDomainDealers(domainId),
    })
}

const domainExportSlice = makeFetchSlice(DOMAIN_EXPORT_REDUCER_NAME)
export const domainExportReducer = domainExportSlice.reducer
export const domainExportActions = domainExportSlice.actions

export const fetchDomainExportThunk = (domainId: string) => {
  return (dispatch: AppDispatch) =>
     fetchEdgeDataThunk({
      dispatch,
      actions: domainExportActions,
      asyncFunc: () => fetchDomainExport(domainId),
    })
}
